import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@eduviaos/ui-kit";
import { useStudentLearningDetail } from "@/hooks/useStudentsLearning";
import { formatRelativeTime } from "@/lib/relativeTime";

export interface StudentLearningDetailDrawerProps {
  studentId: string | null;
  studentName: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function StudentLearningDetailDrawer({
  studentId,
  studentName,
  open,
  onOpenChange,
}: StudentLearningDetailDrawerProps) {
  const detailQuery = useStudentLearningDetail(studentId, open);
  const detail = detailQuery.data;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" side="right">
        <SheetHeader>
          <SheetTitle>{studentName ?? "Learner"} — Learning</SheetTitle>
          <SheetDescription>
            Plans, review queue, certificates, and recent activity from the learner app.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-6">
          {detailQuery.isLoading ? (
            <div className="space-y-3">
              <div className="bg-muted h-16 w-full animate-pulse rounded-md" />
              <div className="bg-muted h-24 w-full animate-pulse rounded-md" />
              <div className="bg-muted h-24 w-full animate-pulse rounded-md" />
            </div>
          ) : detailQuery.isError ? (
            <p className="text-destructive text-sm">Couldn&apos;t load learning detail.</p>
          ) : !detail?.has_portal_login ? (
            <p className="text-muted-foreground text-sm">
              This learner has no portal login yet — learning metrics appear after they accept an
              invite.
            </p>
          ) : (
            <>
              {detail.coach_summary ? (
                <section className="grid grid-cols-2 gap-3 text-sm">
                  <Metric label="Due now" value={detail.coach_summary.due_now} />
                  <Metric label="Reviewed today" value={detail.coach_summary.reviewed_today} />
                  <Metric label="Active plans" value={detail.coach_summary.active_plans} />
                  <Metric label="Streak (days)" value={detail.coach_summary.streak_days} />
                </section>
              ) : null}
              <section>
                <h3 className="mb-2 text-sm font-medium">Plans</h3>
                {detail.pathways.length === 0 ? (
                  <p className="text-muted-foreground text-xs">No active plans.</p>
                ) : (
                  <ul className="divide-border/60 divide-y text-sm">
                    {detail.pathways.map((p) => (
                      <li key={p.id} className="py-2">
                        <div className="font-medium">{p.title}</div>
                        <div className="text-muted-foreground text-xs">
                          {p.status}
                          {p.completion_percent != null
                            ? ` · ${p.completion_percent}% complete`
                            : ""}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              <section>
                <h3 className="mb-2 text-sm font-medium">Certificates</h3>
                {detail.certificates.length === 0 ? (
                  <p className="text-muted-foreground text-xs">None issued yet.</p>
                ) : (
                  <ul className="divide-border/60 divide-y text-sm">
                    {detail.certificates.map((c) => (
                      <li key={c.id} className="py-2">
                        {c.subject} — {c.topic}
                        <span className="text-muted-foreground ml-1 text-xs tabular-nums">
                          ({Math.round(c.score * 100)}%)
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              <section>
                <h3 className="mb-2 text-sm font-medium">Recent activity</h3>
                {detail.recent_activity.length === 0 ? (
                  <p className="text-muted-foreground text-xs">No activity logged.</p>
                ) : (
                  <ul className="divide-border/60 divide-y text-sm">
                    {detail.recent_activity.slice(0, 8).map((a) => (
                      <li key={a.id} className="py-2">
                        <div>{a.title}</div>
                        <div className="text-muted-foreground text-xs">
                          {formatRelativeTime(a.occurred_at)}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="border-border/60 rounded-md border px-3 py-2">
      <div className="text-muted-foreground text-xs">{label}</div>
      <div className="text-lg font-medium tabular-nums">{value}</div>
    </div>
  );
}
